import { ArrowDown, ArrowRight, ArrowUp, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PriceDisplay } from "@/components/ui/price-display";
import { cn } from "@/lib/utils";

import type { DiscountAnalyticsSummaryShape } from "../../types/discount-analytics-types";

function InfoTooltip({ description, colorClass }: { description: string; colorClass: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "shrink-0 inline-flex items-center justify-center rounded-full hover:bg-black/10 p-0.5 transition-colors",
            colorClass
          )}
          aria-label="Info"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-w-xs text-xs p-3 bg-white border-2 border-slate-300 shadow-lg">
        <div className="text-slate-900 whitespace-pre-line leading-relaxed">{description}</div>
      </PopoverContent>
    </Popover>
  );
}

interface Props {
  summary: DiscountAnalyticsSummaryShape;
}

export function LifetimeValueCard({ summary }: Props) {
  const { t } = useTranslation("reports");

  const hasNonDiscountCohort = summary.nonDiscountCustomerCount > 0;
  const delta = summary.lifetimeValueDelta;
  const deltaPositive = delta > 0;
  const deltaZero = delta === 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t("discounts.lifetimeValue.title")}</CardTitle>
        <CardDescription>{t("discounts.lifetimeValue.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-lg border-2 border-rose-200 bg-rose-100 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-rose-900">
                {t("discounts.lifetimeValue.discountCustomers")}
              </span>
              <InfoTooltip
                description={t("discounts.lifetimeValue.discountCustomersTooltip")}
                colorClass="text-rose-700"
              />
            </div>
            <div className="mt-2 text-2xl font-bold text-rose-700">
              <PriceDisplay amount={summary.discountCustomerLifetimeValue} />
            </div>
            <div className="mt-2 text-xs font-medium text-rose-900">
              {t("discounts.lifetimeValue.customerCount", {
                count: summary.discountCustomerCount,
              })}
            </div>
          </div>
          <div
            className={cn(
              "rounded-lg border-2 p-4 shadow-sm",
              hasNonDiscountCohort
                ? "border-slate-300 bg-slate-100"
                : "border-slate-200 bg-slate-50"
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-slate-900">
                {t("discounts.lifetimeValue.nonDiscountCustomers")}
              </span>
              <InfoTooltip
                description={t("discounts.lifetimeValue.nonDiscountCustomersTooltip")}
                colorClass="text-slate-700"
              />
            </div>
            {hasNonDiscountCohort ? (
              <>
                <div className="mt-2 text-2xl font-bold text-slate-800">
                  <PriceDisplay amount={summary.nonDiscountCustomerLifetimeValue} />
                </div>
                <div className="mt-2 text-xs font-medium text-slate-900">
                  {t("discounts.lifetimeValue.customerCount", {
                    count: summary.nonDiscountCustomerCount,
                  })}
                </div>
              </>
            ) : (
              <div className="mt-2 text-xs italic text-slate-700">
                {t("discounts.lifetimeValue.noNonDiscountCohort")}
              </div>
            )}
          </div>
        </div>

        {hasNonDiscountCohort && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border-2 border-slate-200 bg-white p-3 shadow-sm">
            <span className="text-sm font-semibold text-slate-900">
              {t("discounts.lifetimeValue.deltaLabel")}
            </span>
            <Badge
              variant={deltaZero ? "secondary" : deltaPositive ? "default" : "destructive"}
              className="flex items-center gap-1 font-semibold"
            >
              {deltaZero ? (
                <ArrowRight className="h-3 w-3" />
              ) : deltaPositive ? (
                <ArrowUp className="h-3 w-3" />
              ) : (
                <ArrowDown className="h-3 w-3" />
              )}
              <PriceDisplay amount={Math.abs(delta)} />
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
